import { useEffect, useRef } from 'react';
import { useSlotScreenGeneratorStore } from '../store/slotScreenGeneratorStore';
import { loadScreenResultImage, loadVideoFromProject } from '../services/slot-screen-generator';
import { hasProjectRoot } from '../services/FileSystemService';
import { SLOT_SCREEN_ORDER } from '../../core/types/slot-screen-generator.types';

/**
 * Restores blob URLs for screen results and videos that were saved to disk.
 * Runs once on mount — reads each savedPath from disk and
 * updates the store with fresh blob URLs.
 */
export function useRestoreScreenImages() {
  const didRestore = useRef(false);

  useEffect(() => {
    if (didRestore.current || !hasProjectRoot()) return;
    didRestore.current = true;

    const restore = async () => {
      const store = useSlotScreenGeneratorStore.getState();

      let restoredImages = false;

      // Restore screen images
      for (const screenId of SLOT_SCREEN_ORDER) {
        const screen = store.screens[screenId];
        for (const result of screen.generatedResults) {
          if (result.savedPath && !result.imageUrl) {
            const blobUrl = await loadScreenResultImage(result.savedPath);
            if (blobUrl) {
              useSlotScreenGeneratorStore.getState().restoreResultImageUrl(screenId, result.id, blobUrl);
              restoredImages = true;
            }
          }
        }
      }

      // Sync video slots so restored images appear in the storyboard
      if (restoredImages) {
        useSlotScreenGeneratorStore.getState().syncVideoSlotsFromScreens();
      }

      // Restore videos
      for (const entry of store.videoHistory) {
        if (entry.savedPath && !entry.videoUrl) {
          const blobUrl = await loadVideoFromProject(entry.savedPath);
          if (blobUrl) {
            useSlotScreenGeneratorStore.getState().restoreVideoUrl(entry.id, blobUrl);
          }
        }
      }
    };

    restore().catch(() => {});
  }, []);
}
